import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, ShoppingCart, Minus, Plus, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const ProductDetailPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { addToCart } = useCart();
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [quantity, setQuantity] = useState(1);
    const [adding, setAdding] = useState(false);

    useEffect(() => {
        const fetchProduct = async () => {
            try {
                const response = await axios.get(`${API}/api/products/${id}`);
                setProduct(response.data);
            } catch {
                toast.error('Prodotto non trovato');
                navigate('/store');
            } finally {
                setLoading(false);
            }
        };
        fetchProduct();
    }, [id, navigate]);

    const handleAddToCart = async () => {
        if (!user) {
            toast.error('Effettua il login per aggiungere al carrello');
            navigate('/login');
            return;
        }
        
        setAdding(true);
        try {
            await addToCart(product.id, quantity);
            toast.success('Prodotto aggiunto al carrello');
        } catch {
            toast.error('Errore nell\'aggiunta al carrello');
        } finally {
            setAdding(false);
        }
    };

    const getCategoryLabel = (cat) => {
        const labels = {
            new: 'Nuovo',
            refurbished: 'Ricondizionato',
            accessory: 'Accessorio',
            spare_part: 'Ricambio'
        };
        return labels[cat] || cat;
    };

    if (loading) {
        return (
            <div className="min-h-screen py-8">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="animate-pulse">
                        <div className="h-8 w-32 bg-surface rounded mb-8" />
                        <div className="grid lg:grid-cols-2 gap-12">
                            <div className="aspect-square bg-surface rounded-xl" />
                            <div className="space-y-4">
                                <div className="h-8 w-3/4 bg-surface rounded" />
                                <div className="h-6 w-1/2 bg-surface rounded" />
                                <div className="h-32 bg-surface rounded" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!product) return null;

    return (
        <div className="min-h-screen py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Back button */}
                <button
                    onClick={() => navigate(-1)}
                    className="flex items-center gap-2 text-muted hover:text-white mb-8 transition-colors"
                    data-testid="back-button"
                >
                    <ArrowLeft className="w-5 h-5" />
                    Torna indietro
                </button>

                <div className="grid lg:grid-cols-2 gap-12">
                    {/* Image */}
                    <div className="aspect-square bg-surface rounded-xl overflow-hidden border border-border">
                        <img
                            src={product.image_url}
                            alt={product.name}
                            className="w-full h-full object-cover"
                        />
                    </div>

                    {/* Details */}
                    <div className="space-y-6">
                        <div>
                            <span className="text-primary font-medium uppercase tracking-wider text-sm">
                                {getCategoryLabel(product.category)}
                            </span>
                            {product.brand && (
                                <span className="text-muted ml-2">• {product.brand}</span>
                            )}
                            {product.color && (
                                <span className="text-muted ml-2">• {product.color}</span>
                            )}
                        </div>

                        <h1 className="text-3xl sm:text-4xl font-bold" data-testid="product-name">
                            {product.name}
                        </h1>

                        <p className="text-muted text-lg">{product.description}</p>

                        <div className="flex items-baseline gap-4">
                            <span className="text-4xl font-bold text-primary" data-testid="product-price">
                                €{product.price.toFixed(2)}
                            </span>
                            {product.stock > 0 ? (
                                <span className="text-success flex items-center gap-1">
                                    <Check className="w-4 h-4" />
                                    Disponibile ({product.stock} pz)
                                </span>
                            ) : (
                                <span className="text-danger">Non disponibile</span>
                            )}
                        </div>

                        {product.stock > 0 && (
                            <div className="space-y-4">
                                {/* Quantity selector */}
                                <div className="flex items-center gap-4">
                                    <span className="text-muted">Quantità:</span>
                                    <div className="flex items-center gap-2">
                                        <Button
                                            variant="outline"
                                            size="icon"
                                            onClick={() => setQuantity(Math.max(1, quantity - 1))}
                                            disabled={quantity <= 1}
                                            className="border-border"
                                            data-testid="decrease-quantity"
                                        >
                                            <Minus className="w-4 h-4" />
                                        </Button>
                                        <span className="w-12 text-center text-lg font-semibold" data-testid="quantity-value">
                                            {quantity}
                                        </span>
                                        <Button
                                            variant="outline"
                                            size="icon"
                                            onClick={() => setQuantity(Math.min(product.stock, quantity + 1))}
                                            disabled={quantity >= product.stock}
                                            className="border-border"
                                            data-testid="increase-quantity"
                                        >
                                            <Plus className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </div>

                                {/* Add to cart button */}
                                <Button
                                    className="w-full btn-primary text-lg py-6"
                                    onClick={handleAddToCart}
                                    disabled={adding}
                                    data-testid="add-to-cart-btn"
                                >
                                    {adding ? (
                                        'Aggiunta in corso...'
                                    ) : (
                                        <>
                                            <ShoppingCart className="w-5 h-5 mr-2" />
                                            Aggiungi al carrello
                                        </>
                                    )}
                                </Button>
                            </div>
                        )}

                        {/* Features */}
                        <div className="border-t border-border pt-6 space-y-4">
                            <h3 className="font-semibold text-lg">Caratteristiche</h3>
                            <ul className="space-y-2 text-muted">
                                <li className="flex items-center gap-2">
                                    <Check className="w-4 h-4 text-success" />
                                    Garanzia 6 mesi inclusa
                                </li>
                                <li className="flex items-center gap-2">
                                    <Check className="w-4 h-4 text-success" />
                                    Spedizione gratuita sopra i 50€
                                </li>
                                <li className="flex items-center gap-2">
                                    <Check className="w-4 h-4 text-success" />
                                    Reso gratuito entro 14 giorni
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProductDetailPage;
